import {
  Button,
  Checkbox,
  Group,
  Input,
  Stack,
  Text,
  TextInput,
  useMantineTheme,
} from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import { useForm } from '@mantine/form';
import { IconCloudUpload, IconUpload, IconX } from '@tabler/icons-react';
import React, { useRef } from 'react';
import styles from './MutateBrandForm.module.css';

// originalFiles: 前次已經上傳並加入品牌資料的圖片
// uploadedFiles: 這次新增／更新要上傳的圖片
// deletedFiles: 這次更新需要刪除的圖片
export interface MutateBrandFormValues {
  name: string;
  services: string[];
  originalFiles: string[];
  uploadedFiles: FormData[];
  deletedFiles: string[];
}

export type MutateBrandFormInitialValues = Omit<
  MutateBrandFormValues,
  'uploadedFiles' | 'deletedFiles'
>;

interface MutateBrandFormProps {
  initialValues: MutateBrandFormInitialValues;
  onSubmit: (formValues: MutateBrandFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
}

export default function MutateBrandForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitButtonText,
}: MutateBrandFormProps) {
  const theme = useMantineTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<MutateBrandFormValues>({
    mode: 'uncontrolled',
    initialValues: {
      name: initialValues.name,
      services: initialValues.services,
      originalFiles: initialValues.originalFiles,
      uploadedFiles: [],
      deletedFiles: [],
    },
    validate: {
      name: (value) => {
        if (!value.trim()) return ' ';
      },
      services: (value) => {
        if (value.length === 0) return '請至少選擇一個服務類型';
        return null;
      },
      uploadedFiles: (value, values) => {
        // 如果有原始檔案且沒被刪除,則不需要上傳新檔案
        const hasValidOriginalFile =
          values.originalFiles.length > 0 &&
          !values.deletedFiles.includes(values.originalFiles[0]);

        if (!hasValidOriginalFile && value.length === 0) {
          return '請上傳圖片';
        }
        return null;
      },
    },
  });

  // 取得要顯示的圖片 URL
  const getDisplayImageUrl = () => {
    const values = form.getValues();

    // 優先顯示新上傳的檔案
    if (values.uploadedFiles.length > 0) {
      const file = values.uploadedFiles[0].get('file') as File;
      if (file) return URL.createObjectURL(file);
    }

    // 如果有原始檔案且沒被刪除,則顯示原始檔案
    if (
      values.originalFiles.length > 0 &&
      !values.deletedFiles.includes(values.originalFiles[0])
    ) {
      return values.originalFiles[0];
    }

    return null;
  };

  // 處理已經有上傳圖片, 但是還想更新時按下圖片中間的重新上傳按鈕後的反應
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      // 一接收到上傳圖片就轉成 FormData
      const formData = new FormData();
      formData.append('file', file);
      form.setFieldValue('uploadedFiles', [formData]);

      // 如果之前有原始檔案,標記為刪除
      const originalFiles = form.getValues().originalFiles;
      if (originalFiles.length > 0) {
        form.setFieldValue('deletedFiles', [originalFiles[0]]);
      }
    }
  };

  // 點擊「重新上傳」按鈕
  const handleReuploadClick = () => {
    fileInputRef.current?.click();
  };

  // 處理 Dropzone 上傳
  const handleDropzoneUpload = (files: File[]) => {
    if (files.length > 0) {
      // 一接收到上傳圖片就轉成 FormData
      const formDataArray = files.map((file) => {
        const formData = new FormData();
        formData.append('file', file);
        return formData;
      });

      form.setFieldValue('uploadedFiles', formDataArray);

      // 如果之前有原始檔案,標記為刪除
      const originalFiles = form.getValues().originalFiles;
      if (originalFiles.length > 0) {
        form.setFieldValue('deletedFiles', [originalFiles[0]]);
      }
    }
  };

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const displayImageUrl = getDisplayImageUrl();
  const imageError = form.errors.uploadedFiles;
  const servicesError = form.errors.services;

  // 勾選欄位的錯誤顯示: 紅色 input 框
  const checkboxErrorStyles = servicesError
    ? {
        input: { borderColor: 'var(--mantine-color-red-6)' },
      }
    : undefined;

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          {/* 品牌名稱 */}
          <TextInput
            label="品牌名稱"
            placeholder="請輸入品牌名稱"
            withAsterisk
            key={form.key('name')}
            {...form.getInputProps('name')}
            styles={{
              label: { marginBottom: 8 },
              input: {
                paddingLeft: 10,
                borderColor: form.errors.name
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />

          {/* 服務類型 */}
          <Checkbox.Group
            label="服務類型"
            withAsterisk
            key={form.key('services')}
            {...form.getInputProps('services')}
            styles={{
              error: { marginTop: 10 },
            }}
          >
            <Group mt="xs" ml="xs" mr="xs">
              <Checkbox
                value="PARKING"
                label="停車場"
                styles={checkboxErrorStyles}
              />
              <Checkbox
                value="FUELING"
                label="加油站"
                styles={checkboxErrorStyles}
              />
              <Checkbox
                value="CHARGING"
                label="充電站"
                styles={checkboxErrorStyles}
              />
              <Checkbox
                value="MAINTENANCE"
                label="保修廠"
                styles={checkboxErrorStyles}
              />
              <Checkbox
                value="INSPECTION"
                label="驗車場"
                styles={checkboxErrorStyles}
              />
            </Group>
          </Checkbox.Group>

          {/* 品牌圖片 */}
          <Input.Wrapper
            label="品牌圖片"
            required
            error={imageError}
            styles={{
              label: {
                marginBottom: 8,
                fontWeight: 500,
              },
              error: { marginTop: 10 },
            }}
          >
            {displayImageUrl ? (
              <>
                {/* 隱藏的檔案輸入 */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />

                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: 200,
                    overflow: 'hidden',
                    borderRadius: 8,
                    cursor: 'pointer',
                  }}
                  className={styles.brandImageContainer}
                >
                  {/* 預覽圖片 */}
                  <img
                    src={displayImageUrl}
                    alt="brand preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    className={styles.brandPreviewImg}
                  />

                  <Button
                    variant="filled"
                    color="dark"
                    size="sm"
                    onClick={handleReuploadClick}
                    onMouseDown={(e) => e.preventDefault()}
                    className={styles.brandPreviewBtn}
                  >
                    重新上傳圖片
                  </Button>
                </div>
              </>
            ) : (
              <Dropzone
                onDrop={handleDropzoneUpload}
                onReject={(files) => console.log('rejected files', files)}
                maxSize={5 * 1024 ** 2}
                accept={[MIME_TYPES.png]}
                style={{
                  border: imageError
                    ? '1px solid var(--mantine-color-red-6)'
                    : '1px dashed var(--mantine-color-gray-4)',
                  borderRadius: 8,
                  padding: 0,
                }}
              >
                <Group justify="center" gap="xl" mih={220}>
                  <Stack align="center">
                    <Dropzone.Accept>
                      <IconUpload
                        size={52}
                        color={theme.colors.blue[6]}
                        stroke={1.5}
                      />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                      <IconX
                        size={52}
                        color={theme.colors.red[6]}
                        stroke={1.5}
                      />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                      <IconCloudUpload size={52} color="#212529" stroke={1.5} />
                    </Dropzone.Idle>
                    <div>
                      <Text size="lg" fw={500} inline mb={12} ta="center">
                        上傳圖片
                      </Text>
                      <Text size="sm" c="dimmed" inline mt={7} ta="center">
                        把 .png 檔案拖曳到這裡上傳,或是選擇圖片。
                      </Text>
                    </div>
                  </Stack>
                </Group>
              </Dropzone>
            )}
          </Input.Wrapper>
        </Stack>

        {/* 取消 與 新增 or 更新按鈕 */}
        <Group justify="flex-end" mt="xl">
          <Button variant="outline" onClick={handleCancel}>
            取消
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            {submitButtonText}
          </Button>
        </Group>
      </form>
    </>
  );
}
